import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { defaultWMS1100_R01DetailDto, defaultWMS1100_R01HeaderDto, defaultWMS1100_R01LotDto, WMS1100_R01DetailDto, WMS1100_R01HeaderDto, WMS1100_R01LotDto } from './Dto_WMS1100_R01';
import { showWarning } from 'src/utils/toast/toastUtil';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';
import useUserStore from 'src/store/useUserStore';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';


export interface WMS1100_R01Filter {
	
	dateRange: DateRange | undefined;
	customCd: string;
	customNm: string;
}
export interface Store_WMS1100_R01 {
	WMS1100_R01Filter: WMS1100_R01Filter;
	WMS1100_R01PastFilter: WMS1100_R01Filter;
	setWMS1100_R01Filter: (filter: Partial<Store_WMS1100_R01['WMS1100_R01Filter']>) => void;
	setWMS1100_R01PastFilter: (filter: Store_WMS1100_R01['WMS1100_R01PastFilter']) => void;
	
	// ====== 매출 리스트 및 선택/편집 ======
	WMS1100_R01List: WMS1100_R01HeaderDto[];
	setWMS1100_R01List: (dataList: WMS1100_R01HeaderDto[]) => void;

	selectedWMS1100_R01: WMS1100_R01HeaderDto | undefined;
	setSelectedWMS1100_R01: (data: WMS1100_R01HeaderDto | undefined) => void;
	initSelectedWMS1100_R01: () => void;

	// ====== 매출 상세(디테일) 데이터 ======
	WMS1100_R01DetailList: WMS1100_R01DetailDto[];
	setWMS1100_R01DetailList: (data: WMS1100_R01DetailDto[]) => void;

	// ====== 선택(디테일) 행 선택/삭제 관리 ======
	selectedWMS1100_R01DetailList: string[];
	setSelectedWMS1100_R01DetailList: (idList: string[]) => void;
	initSelectedWMS1100_R01DetailList: () => void;

	originalWMS1100_R01DetailList: WMS1100_R01DetailDto[];
	setOriginalWMS1100_R01DetailList: (data: WMS1100_R01DetailDto[]) => void;

	// ====== 입고 상세(lot) 데이터 ======
	WMS1100_R01LotList: WMS1100_R01LotDto[];
	setWMS1100_R01LotList: (data: WMS1100_R01LotDto[]) => void;

	// ====== 입고 상세선택(lot) 행 선택/삭제 관리 ======
	selectedWMS1100_R01LotList: string[];
	setSelectedWMS1100_R01LotList: (idList: string[]) => void;
	initSelectedWMS1100_R01LotList: () => void;

	originalWMS1100_R01LotList: WMS1100_R01LotDto[];
	setOriginalWMS1100_R01LotList: (data: WMS1100_R01LotDto[]) => void;

	// ====== 컬럼 상태(그리드) ======
	columnState: ColumnState[] | null;			setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;	setDetailColumnState: (state: ColumnState[] | null) => void;
	lotColumnState: ColumnState[] | null;		setLotColumnState: (state: ColumnState[] | null) => void;
	
	allCount: number;				setAllCount: (allCount: number) => void;

	fetchLotTrigger: boolean;		setFetchLotTrigger: (fetchLotTrigger: boolean) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
	lotScrollTop: number | null;
	setLotScrollTop: (top: number | null) => void;

	// ====== 초기화 ======
	initData: () => void;

}

export const defaultWMS1100_R01Filter : WMS1100_R01Filter = {
	
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	customCd: '',
	customNm: '',
};

const getInitialWMS1100_R01HeaderDto = (): WMS1100_R01HeaderDto => {
	const { wmsCdList, warehouseCdList } = useCommonCodeStore.getState();
	return {
		...defaultWMS1100_R01HeaderDto,
		logicCenter: wmsCdList?.[0]?.minorCd || '',
		warehouseCd: warehouseCdList?.[0]?.warehouseCd || '',
		warehouseNm: warehouseCdList?.[0]?.warehouseNm || '',
	};
};

export const useWMS1100_R01Store = create<Store_WMS1100_R01>((set, get) => ({
	WMS1100_R01Filter: defaultWMS1100_R01Filter,
	WMS1100_R01PastFilter: defaultWMS1100_R01Filter,
	setWMS1100_R01Filter: (update) => set((state) => ({
		WMS1100_R01Filter: {
			...state.WMS1100_R01Filter,
			...update
		}
	})),
	setWMS1100_R01PastFilter: (filter: Store_WMS1100_R01['WMS1100_R01PastFilter']) => set((state) => {
		return {
			...state,
			WMS1100_R01PastFilter: filter
		}
	}),

	// ====== 매출 리스트 및 선택/편집 ======
	WMS1100_R01List: [],
	setWMS1100_R01List: (dataList: WMS1100_R01HeaderDto[]) => set((state) => {
		return {
			...state,
			WMS1100_R01List: dataList
		}
	}),

	selectedWMS1100_R01: undefined,
	setSelectedWMS1100_R01: (data: WMS1100_R01HeaderDto | undefined) => set((state) => {

		return { 
			selectedWMS1100_R01: data, 
			selectedWMS1100_R01DetailList: [], 
			selectedWMS1100_R01LotList: [],
			detailScrollTop: null,
			lotScrollTop: null,
		};
	}),
	initSelectedWMS1100_R01: () => {
		set({ 
			selectedWMS1100_R01: undefined, 
			WMS1100_R01DetailList: [] 
		})
	},

	// ====== 매출 상세(디테일) 데이터 ======
	WMS1100_R01DetailList: [],
	setWMS1100_R01DetailList: (data: WMS1100_R01DetailDto[]) => set((state) => {
		let newList: WMS1100_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				//...defaultWMS1100_R01DetailDto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS1100_R01DetailList: newList
		}
	}),

	selectedWMS1100_R01DetailList: [],
	setSelectedWMS1100_R01DetailList: (idList: string[]) => set((state) => {
		const firstItemCd = state.WMS1100_R01DetailList.find(
			(item) => item.valueIndex.toString() === idList[0]
		)?.itemCd || null;

		return {
			...state,
			// 상세 행이 바뀌면 이전 Lot 선택 상태는 초기화
			selectedWMS1100_R01LotList: [],
			selectedWMS1100_R01DetailList: idList,
			selectedItemCdForLot: firstItemCd,
			lotScrollTop: null,
		}
	}),
	addSelectedWMS1100_R01DetailList: (idList: string[]) => set((state) => {
		const _selectedWMS1100_R01DetailList = [...state.selectedWMS1100_R01DetailList];
		if(idList.length > 0) {
			_selectedWMS1100_R01DetailList.push(...idList);
		}
		return {
			...state,
			selectedWMS1100_R01DetailList: _selectedWMS1100_R01DetailList
		}
	}),
	removeSelectedWMS1100_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		selectedWMS1100_R01DetailList: [...state.selectedWMS1100_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedWMS1100_R01DetailList: () => set({ selectedWMS1100_R01DetailList: [] }),

	originalWMS1100_R01DetailList: [],
	setOriginalWMS1100_R01DetailList: (data: WMS1100_R01DetailDto[]) => set((state) => {
		let newList: WMS1100_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item, index) => {
			newList.push({ 
				...defaultWMS1100_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalWMS1100_R01DetailList: newList
		}
	}),

	// ====== 입고 상세(lot) 데이터 ======
	WMS1100_R01LotList: [],
	setWMS1100_R01LotList: (data: WMS1100_R01LotDto[]) => set((state) => {
		let newList: WMS1100_R01LotDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item, index) => {
			// console.log(index + '번째 행의 item.Index 정보: ' 
			// 	+ ' valueIndex : ' + item.valueIndex 
			// 	+ ' valueAndOrderIndex : ' + valueAndOrderIndex
			// 	+ ' orderIndex : ' + item.orderIndex 
			// 	+ ' orderIndexSeq : ' + item.orderIndexSeq);
			newList.push({ 
				...defaultWMS1100_R01LotDto,
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS1100_R01LotList: data
		}
	}),

	selectedWMS1100_R01LotList: [],
	setSelectedWMS1100_R01LotList: (idList: string[]) => {
		set((state) => {
			return {
				...state,
				selectedWMS1100_R01LotList: idList,
			}
		});
	},
	initSelectedWMS1100_R01LotList: () => set({ selectedWMS1100_R01LotList: [] }),

	originalWMS1100_R01LotList: [],
	setOriginalWMS1100_R01LotList: (data: WMS1100_R01LotDto[]) => set((state) => {
		let newList: WMS1100_R01LotDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item, index) => {
			newList.push({ 
				...defaultWMS1100_R01LotDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalWMS1100_R01LotList: newList
		}
	}),

	// ====== 컬럼 상태(그리드) ======
	columnState: null,				setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),
	detailColumnState: null,		setDetailColumnState: (state: ColumnState[] | null) => set({ detailColumnState: state }),
	lotColumnState: null,			setLotColumnState: (state: ColumnState[] | null) => set({ lotColumnState: state }),
	
	allCount: 0,					setAllCount: (allCount: number) => set({ allCount }),

	fetchLotTrigger: false,			setFetchLotTrigger: (fetchLotTrigger: boolean) => set({ fetchLotTrigger }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),
	lotScrollTop: null,
	setLotScrollTop: (top: number | null) => set({ lotScrollTop: top }),

	// ====== 초기화 ======
	initData: () => {
		set((state) => ({
		...state,

		WMS1100_R01Filter: defaultWMS1100_R01Filter,
		WMS1100_R01PastFilter: defaultWMS1100_R01Filter,

		WMS1100_R01List: [],
		selectedWMS1100_R01: undefined,
		originalWMS1100_R01List: [],

		WMS1100_R01LotMap: new Map<string, WMS1100_R01LotDto[]>(),
		originalWMS1100_R01LotList: [],
		selectedItemCdForLot: null,

		
		WMS1100_R01DetailList: [],
		selectedWMS1100_R01DetailList: [],
		originalWMS1100_R01DetailList: [],
		editWMS1100_R01: getInitialWMS1100_R01HeaderDto(),
		allCount: 0,
		

		transportComplete: false,
		fetchLotTrigger: false,
		scrollTop: null,
		detailScrollTop: null,
		lotScrollTop: null,
	}))},

}));
